import { gql, useMutation, useQuery } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useTimesheetApi custom hook for timesheet operations.
 * - Provides CRUD and workflow operations for timesheets using Apollo Client and GraphQL.
 * - Handles fetching fridays, adding, approving, and rejecting timesheets.
 * - Supports fetching timesheets by week, resource, and project.
 * - Returns helpers for all timesheet-related operations.
 * - Handles error logging and ensures UI stays in sync with backend.
 * - Designed for weekly/project/resource timesheet management.
 * - Each method returns a standardized object: {success, data, error}.
 * - All GraphQL queries use "network-only" fetch policy for fresh data.
 * - Mutations are wrapped with try/catch for robust error handling.
 * - Approval/rejection flows support both project and resource level.
 * - Useful for dashboards, timesheet entry, approval, and reporting screens.
 */

// Define GraphQL Queries and Mutations
const TIMESHEET_QUERIES = {
  GET_FRIDAYS: gql`
    query GetFridays($lastFriday: Int!, $upcomingFriday: Int) {
      getFridays(lastFriday: $lastFriday, upcomingFriday: $upcomingFriday) {
        label
        value
        weekNumber
      }
    }
  `,
  GET_FRIDAYS_BY_QUARTER: gql`
    query GetFridaysByQuarter($quarter: String!) {
      getFridaysByQuarter(quarter: $quarter) {
        label
        value
        weekNumber
      }
    }
  `,
  GET_FRIDAYS_BY_YEAR_AND_MONTH: gql`
    query GetFridaysByYearAndMonth($yearAndMonth: String!) {
      getFridaysByYearAndMonth(year_and_month: $yearAndMonth) {
        label
        value
        weekNumber
      }
    }
  `,
  GET_LATEST_AND_OLDEST_TIMESHEET_LOGGED_DATE: gql`
    query GetLatestAndOldestTimesheetLoggedDate {
      getLatestAndOldestTimesheetLoggedDate {
        latest_logged_date
        oldest_logged_date
      }
    }
  `,
  ADD_WEEKLY_TIMESHEET: gql`
    mutation AddWeeklyTimesheet($weeklyTimesheetDetails: [WeeklyTimesheetInput]) {
      AddWeeklyTimesheet(weeklyTimesheetDetails: $weeklyTimesheetDetails) {
        project_id
        task_id
        resource_id
        timesheet_week
        resource_actual_hours
        resource_budget_hours
        is_draft
        is_submitted
        resource_timesheets {
          timesheet_week
          timesheet_day
          logged_date
          logged_hours
          timesheet_id
          milestone_activity_mapping_id
          activity {
            activity_id
            activity_name
            activity_code
          }
        }
        total_hours_for_week
        weekly_timesheet_id
        project_name
        task_name
        milestone_activity_mapping_id
        project_manager_comments {
          id
          weekly_timesheet_id
          comment
          created_on
          updated_on
          resource_id
          resource_name
          type
        }
        updated_on
        resource_comments {
          id
          weekly_timesheet_id
          resource_id
          resource_name
          comment
          type
          created_on
          updated_on
        }
      }
    }
  `,
  GET_RESOURCE_TIMESHEET: gql`
    query GetResourceTimesheetByWeekNumber(
      $timesheetWeek: String!
      $resourceId: ID!
    ) {
      getResourceTimesheetByWeekNumber(
        timesheet_week: $timesheetWeek
        resource_id: $resourceId
      ) {
        project_id
        task_id
        resource_id
        timesheet_week
        resource_actual_hours
        resource_budget_hours
        is_draft
        is_submitted
        resource_timesheets {
          timesheet_id
          timesheet_week
          timesheet_day
          logged_date
          logged_hours
          milestone_activity_mapping_id
          activity {
            activity_id
            activity_name
            activity_code
          }
        }
        total_hours_for_week
        weekly_timesheet_id
        task_name
        project_name
        milestone_activity_mapping_id
        project_manager_comments {
          id
          weekly_timesheet_id
          comment
          created_on
          updated_on
          resource_id
          resource_name
          type
        }
        updated_on
        resource_comments {
          id
          weekly_timesheet_id
          resource_id
          resource_name
          comment
          type
          created_on
          updated_on
        }
      }
    }
  `,
  UPDATE_TIMESHEET_REJECTION: gql`
    mutation UpdateTimesheetRejection(
      $timesheetWeek: String!
      $loggedBy: Int!
      $status: TimesheetStatusCode!
      $rejectedBy: Int!
      $rejectedComments: [WeeklyTimesheetComments]
      $projectId: Int!
    ) {
      UpdateTimesheetRejection(
        timesheet_week: $timesheetWeek
        logged_by: $loggedBy
        status: $status
        rejected_by: $rejectedBy
        rejectedComments: $rejectedComments
        project_id: $projectId
      ) {
        project_id
        task_id
        resource_id
        timesheet_week
        resource_actual_hours
        resource_budget_hours
        is_draft
        is_submitted
        resource_timesheets {
          timesheet_id
          timesheet_week
          timesheet_day
          logged_date
          logged_hours
          milestone_activity_mapping_id
          activity {
            activity_id
            activity_name
            activity_code
          }
        }
        total_hours_for_week
        weekly_timesheet_id
        project_name
        task_name
        project_manager_comments {
          id
          weekly_timesheet_id
          comment
          created_on
          updated_on
          resource_id
          resource_name
          type
        }
        updated_on
        resource_comments {
          id
          weekly_timesheet_id
          resource_id
          resource_name
          comment
          type
          created_on
          updated_on
        }
      }
    }
  `,
  GET_RESOURCE_TIMESHEETS_BY_WEEK_AND_PROJECT: gql`
    query GetResourceTimesheetsByWeekIdAndProjectId(
      $timesheetWeek: String!
      $projectId: ID!
    ) {
      getResourceTimesheetsByWeekIdAndProjectId(
        timesheet_week: $timesheetWeek
        project_id: $projectId
      ) {
        project_id
        resource_id
        timesheet_week
        total_actual_hours
        total_budget_hours
        total_weekly_hours {
          timesheet_day
          hours
        }
        total_hours
        is_approved
        is_rejected
        auto_approve
        resource_weekly_timesheets {
          project_id
          task_id
          resource_id
          timesheet_week
          resource_actual_hours
          resource_budget_hours
          is_draft
          is_submitted
          resource_timesheets {
            timesheet_id
            timesheet_week
            timesheet_day
            logged_date
            logged_hours
            milestone_activity_mapping_id
            activity {
              activity_id
              activity_name
              activity_code
            }
          }
          total_hours_for_week
          weekly_timesheet_id
          project_name
          task_name
          project_manager_comments {
            id
            weekly_timesheet_id
            comment
            created_on
            updated_on
            resource_id
            resource_name
            type
          }
          updated_on
          resource_comments {
            id
            weekly_timesheet_id
            resource_id
            resource_name
            comment
            type
            created_on
            updated_on
          }
        }
        resource_name
      }
    }
  `,
  GET_RESOURCE_TIMESHEETS_BY_WEEK_AND_RESOURCE: gql`
    query GetResourceTimesheetsByWeekIdAndResourceId(
      $timesheetWeek: String!
      $resourceId: ID!
    ) {
      getResourceTimesheetsByWeekIdAndResourceId(
        timesheet_week: $timesheetWeek
        resource_id: $resourceId
      ) {
        project_id
        resource_id
        resource_name
        timesheet_week
        total_actual_hours
        total_budget_hours
        total_weekly_hours {
          timesheet_day
          hours
        }
        total_hours
        is_approved
        is_rejected
        auto_approve
        resource_weekly_timesheets {
          project_id
          project_name
          task_id
          task_name
          resource_id
          timesheet_week
          resource_actual_hours
          resource_budget_hours
          project_manager_comments {
            id
            weekly_timesheet_id
            resource_id
            resource_name
            comment
            type
            created_on
            updated_on
          }
          resource_comments {
            id
            weekly_timesheet_id
            resource_id
            resource_name
            comment
            type
            created_on
            updated_on
          }
          is_draft
          is_submitted
          resource_timesheets {
            timesheet_id
            timesheet_week
            timesheet_day
            logged_date
            logged_hours
            milestone_activity_mapping_id
            activity {
              activity_id
              activity_name
              activity_code
            }
          }
          total_hours_for_week
          weekly_timesheet_id
          milestone_activity_mapping_id
          updated_on
        }
      }
    }
  `,
  UPDATE_TIMESHEET_APPROVE: gql`
    mutation UpdateTimesheetApprove(
      $timesheetWeek: String!
      $loggedBy: Int!
      $status: TimesheetStatusCode!
      $approvedBy: Int!
      $weeklyTimesheetId: [Int!]!
      $projectId: Int!
    ) {
      UpdateTimesheetApprove(
        timesheet_week: $timesheetWeek
        logged_by: $loggedBy
        status: $status
        approved_by: $approvedBy
        weekly_timesheet_id: $weeklyTimesheetId
        project_id: $projectId
      ) {
        project_id
        project_name
        task_id
        task_name
        resource_id
        timesheet_week
        resource_actual_hours
        resource_budget_hours
        project_manager_comments {
          id
          weekly_timesheet_id
          resource_id
          resource_name
          comment
          type
          created_on
          updated_on
        }
        resource_comments {
          id
          weekly_timesheet_id
          resource_id
          resource_name
          comment
          type
          created_on
          updated_on
        }
        is_draft
        is_submitted
        resource_timesheets {
          timesheet_id
          timesheet_week
          timesheet_day
          logged_date
          logged_hours
          milestone_activity_mapping_id
          activity {
            activity_id
            activity_name
            activity_code
          }
        }
        total_hours_for_week
        weekly_timesheet_id
        milestone_activity_mapping_id
        updated_on
      }
    }
  `,
  UPDATE_RESOURCE_TIMESHEET_REJECTION: gql`
    mutation UpdateResourceTimesheetRejection(
      $timesheetWeek: String!
      $resourceId: Int!
      $status: TimesheetStatusCode!
      $rejectedBy: Int!
      $rejectedComments: [WeeklyTimesheetComments]
    ) {
      UpdateResourceTimesheetRejection(
        timesheet_week: $timesheetWeek
        resource_id: $resourceId
        status: $status
        rejected_by: $rejectedBy
        rejectedComments: $rejectedComments
      ) {
        project_id
        resource_id
        resource_name
        timesheet_week
        total_actual_hours
        total_budget_hours
        total_weekly_hours {
          timesheet_day
          hours
        }
        total_hours
        is_approved
        is_rejected
        auto_approve
        resource_weekly_timesheets {
          project_id
          project_name
          task_id
          task_name
          resource_id
          timesheet_week
          resource_actual_hours
          resource_budget_hours
          project_manager_comments {
            id
            weekly_timesheet_id
            resource_id
            resource_name
            comment
            type
            created_on
            updated_on
          }
          resource_comments {
            id
            weekly_timesheet_id
            resource_id
            resource_name
            comment
            type
            created_on
            updated_on
          }
          is_draft
          is_submitted
          resource_timesheets {
            timesheet_id
            timesheet_week
            timesheet_day
            logged_date
            logged_hours
            milestone_activity_mapping_id
            activity {
              activity_id
              activity_name
              activity_code
            }
          }
          total_hours_for_week
          weekly_timesheet_id
          milestone_activity_mapping_id
          updated_on
        }
      }
    }
  `,
  UPDATE_RESOURCE_TIMESHEET_APPROVE: gql`
    mutation UpdateResourceTimesheetApprove(
      $timesheetWeek: String!
      $resourceId: Int!
      $status: TimesheetStatusCode!
      $approvedBy: Int!
    ) {
      UpdateResourceTimesheetApprove(
        timesheet_week: $timesheetWeek
        resource_id: $resourceId
        status: $status
        approved_by: $approvedBy
      ) {
        project_id
        resource_id
        resource_name
        timesheet_week
        total_actual_hours
        total_budget_hours
        total_weekly_hours {
          timesheet_day
          hours
        }
        total_hours
        is_approved
        is_rejected
        auto_approve
        resource_weekly_timesheets {
          project_id
          project_name
          task_id
          task_name
          resource_id
          timesheet_week
          resource_actual_hours
          resource_budget_hours
          project_manager_comments {
            id
            weekly_timesheet_id
            resource_id
            resource_name
            comment
            type
            created_on
            updated_on
          }
          resource_comments {
            id
            weekly_timesheet_id
            resource_id
            resource_name
            comment
            type
            created_on
            updated_on
          }
          is_draft
          is_submitted
          resource_timesheets {
            timesheet_id
            timesheet_week
            timesheet_day
            logged_date
            logged_hours
            milestone_activity_mapping_id
            activity {
              activity_id
              activity_name
              activity_code
            }
          }
          total_hours_for_week
          weekly_timesheet_id
          updated_on
        }
      }
    }
  `,
  DELETE_TIMESHEET: gql`
  mutation DeleteWeeklyTimesheetById($weekly_timesheet_id: ID!){
  DeleteWeeklyTimesheetById(weekly_timesheet_id:$weekly_timesheet_id){
     project_id
        task_id
        resource_id
        timesheet_week
        resource_actual_hours
        resource_budget_hours
        is_draft
        is_submitted
        resource_timesheets {
          timesheet_week
          timesheet_day
          logged_date
          logged_hours
          timesheet_id
          milestone_activity_mapping_id
          activity {
            activity_id
            activity_name
            activity_code
          }
        }
        total_hours_for_week
        weekly_timesheet_id
        project_name
        task_name
        milestone_activity_mapping_id
        project_manager_comments {
          id
          weekly_timesheet_id
          comment
          created_on
          updated_on
          resource_id
          resource_name
          type
        }
        updated_on
        resource_comments {
          id
          weekly_timesheet_id
          resource_id
          resource_name
          comment
          type
          created_on
          updated_on
        }
      }
    }
  `
};

export const useTimesheetApi = () => {
  /**
   * Fetches all fridays between two dates.
   * @param {number} lastFriday
   * @param {number} upcomingFriday
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getFridays = async (lastFriday, upcomingFriday) => {
    try {
      const { data } = await client.query({
        query: TIMESHEET_QUERIES.GET_FRIDAYS,
        variables: { lastFriday, upcomingFriday },
        fetchPolicy: "network-only",
      });

      if (!data?.getFridays) {
        throw new Error("No fridays data received");
      }

      return { success: true, data: data.getFridays };
    } catch (error) {
      console.error("Error fetching fridays:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches Friday week options for a fiscal quarter string (e.g. 2024_Q1).
   * @param {string} quarter
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getFridaysByQuarter = async (quarter) => {
    try {
      const { data } = await client.query({
        query: TIMESHEET_QUERIES.GET_FRIDAYS_BY_QUARTER,
        variables: { quarter },
        fetchPolicy: "network-only",
      });

      if (!data?.getFridaysByQuarter) {
        throw new Error("No fridays by quarter data received");
      }

      return { success: true, data: data.getFridaysByQuarter };
    } catch (error) {
      console.error("Error fetching getFridaysByQuarter:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches Friday week options for a year+month string (e.g. 2026-01).
   * @param {string} yearAndMonth
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getFridaysByYearAndMonth = async (yearAndMonth) => {
    try {
      const { data } = await client.query({
        query: TIMESHEET_QUERIES.GET_FRIDAYS_BY_YEAR_AND_MONTH,
        variables: { yearAndMonth },
        fetchPolicy: "network-only",
      });

      if (!data?.getFridaysByYearAndMonth) {
        throw new Error("No fridays by year and month data received");
      }

      return { success: true, data: data.getFridaysByYearAndMonth };
    } catch (error) {
      console.error("Error fetching getFridaysByYearAndMonth:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches the latest and oldest timesheet logged dates.
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const getLatestAndOldestTimesheetLoggedDate = async () => {
    try {
      const { data } = await client.query({
        query: TIMESHEET_QUERIES.GET_LATEST_AND_OLDEST_TIMESHEET_LOGGED_DATE,
        fetchPolicy: "network-only",
      });

      if (!data?.getLatestAndOldestTimesheetLoggedDate) {
        throw new Error("No latest/oldest timesheet logged date received");
      }

      return { success: true, data: data.getLatestAndOldestTimesheetLoggedDate };
    } catch (error) {
      console.error("Error fetching getLatestAndOldestTimesheetLoggedDate:", error);
      return { success: false, error: error.message };
    }
  };

  // Add weekly timesheet mutation
  const [addWeeklyTimesheetMutation] = useMutation(
    TIMESHEET_QUERIES.ADD_WEEKLY_TIMESHEET
  );

  /**
   * Adds a new weekly timesheet.
   * @param {Array} weeklyTimesheetDetails
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const addWeeklyTimesheet = async (weeklyTimesheetDetails) => {
    // Log for debugging
    console.log("weeklyTimesheetDetails", weeklyTimesheetDetails);
    try {
      const { data } = await addWeeklyTimesheetMutation({
        variables: { weeklyTimesheetDetails },
      });

      if (!data?.AddWeeklyTimesheet) {
        throw new Error("Failed to add weekly timesheet");
      }

      return {
        success: true,
        data: data.AddWeeklyTimesheet,
      };
    } catch (error) {
      console.error("Error adding weekly timesheet:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  };

  /**
   * Fetches a resource's timesheet for a given week.
   * @param {string} timesheetWeek
   * @param {string|number} resourceId
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const getResourceTimesheet = async (timesheetWeek, resourceId) => {
    try {
      const { data } = await client.query({
        query: TIMESHEET_QUERIES.GET_RESOURCE_TIMESHEET,
        variables: { timesheetWeek, resourceId },
        fetchPolicy: "network-only",
      });

      if (!data?.getResourceTimesheetByWeekNumber) {
        throw new Error("No timesheet data received");
      }

      return { success: true, data: data.getResourceTimesheetByWeekNumber };
    } catch (error) {
      console.error("Error fetching resource timesheet:", error);
      return { success: false, error: error.message };
    }
  };

  // Timesheet rejection mutation
  const [updateTimesheetRejectionMutation] = useMutation(
    TIMESHEET_QUERIES.UPDATE_TIMESHEET_REJECTION
  );

  /**
   * Updates a timesheet as rejected (project-level).
   * @param {Object} rejectionData
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateTimesheetRejection = async (rejectionData) => {
    try {
      const { data } = await updateTimesheetRejectionMutation({
        variables: {
          timesheetWeek: rejectionData.timesheet_week,
          loggedBy: rejectionData.logged_by,
          status: rejectionData.status,
          rejectedBy: rejectionData.rejected_by,
          rejectedComments: rejectionData.rejectedComments,
          projectId: rejectionData.project_id,
        },
      });

      if (!data?.UpdateTimesheetRejection) {
        throw new Error("Failed to update timesheet rejection");
      }

      return {
        success: true,
        data: data.UpdateTimesheetRejection,
      };
    } catch (error) {
      console.error("Error updating timesheet rejection:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  };

  // Timesheet approval mutation
  const [updateTimesheetApproveMutation] = useMutation(
    TIMESHEET_QUERIES.UPDATE_TIMESHEET_APPROVE
  );

const [deleteTimesheetMutation] = useMutation(TIMESHEET_QUERIES.DELETE_TIMESHEET);
  /**
   * Approves a timesheet (project-level).
   * @param {Object} approvalData
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateTimesheetApprove = async (approvalData) => {
    try {
      const { data } = await updateTimesheetApproveMutation({
        variables: {
          timesheetWeek: approvalData.timesheet_week,
          loggedBy: approvalData.logged_by,
          status: approvalData.status,
          approvedBy: approvalData.approved_by,
          weeklyTimesheetId: approvalData.weekly_timesheet_id,
          projectId: approvalData.project_id,
        },
      });

      if (!data?.UpdateTimesheetApprove) {
        throw new Error("Failed to approve timesheet");
      }

      return {
        success: true,
        data: data.UpdateTimesheetApprove,
      };
    } catch (error) {
      console.error("Error approving timesheet:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  };

  /**
   * Fetches all resource timesheets for a week and project.
   * @param {string} timesheetWeek
   * @param {string|number} projectId
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getResourceTimesheetsByWeekAndProject = async (
    timesheetWeek,
    projectId
  ) => {
    try {
      const { data } = await client.query({
        query: TIMESHEET_QUERIES.GET_RESOURCE_TIMESHEETS_BY_WEEK_AND_PROJECT,
        variables: { timesheetWeek, projectId },
        fetchPolicy: "network-only",
      });

      if (!data?.getResourceTimesheetsByWeekIdAndProjectId) {
        throw new Error("No timesheet data received");
      }

      return {
        success: true,
        data: data.getResourceTimesheetsByWeekIdAndProjectId,
      };
    } catch (error) {
      console.error("Error fetching resource timesheets:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches all resource timesheets for a week and resource.
   * @param {string} timesheetWeek
   * @param {string|number} resourceId
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getResourceTimesheetsByWeekAndResource = async (timesheetWeek, resourceId) => {
    try {
      const { data } = await client.query({
        query: TIMESHEET_QUERIES.GET_RESOURCE_TIMESHEETS_BY_WEEK_AND_RESOURCE,
        variables: { timesheetWeek, resourceId },
        fetchPolicy: "network-only",
      });

      if (!data?.getResourceTimesheetsByWeekIdAndResourceId) {
        throw new Error("No timesheet data received");
      }

      return {
        success: true,
        data: data.getResourceTimesheetsByWeekIdAndResourceId,
      };
    } catch (error) {
      console.error("Error fetching resource timesheets:", error);
      return { success: false, error: error.message };
    }
  };

  // Resource timesheet rejection mutation
  const [updateResourceTimesheetRejectionMutation] = useMutation(
    TIMESHEET_QUERIES.UPDATE_RESOURCE_TIMESHEET_REJECTION
  );

  /**
   * Updates a resource timesheet as rejected (resource-level).
   * @param {Object} rejectionData
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateResourceTimesheetRejection = async (rejectionData) => {
    try {
      const { data } = await updateResourceTimesheetRejectionMutation({
        variables: {
          timesheetWeek: rejectionData.timesheet_week,
          resourceId: rejectionData.resource_id,
          status: rejectionData.status,
          rejectedBy: rejectionData.rejected_by,
          rejectedComments: rejectionData.rejectedComments,
        },
      });

      if (!data?.UpdateResourceTimesheetRejection) {
        throw new Error("Failed to update resource timesheet rejection");
      }

      return {
        success: true,
        data: data.UpdateResourceTimesheetRejection,
      };
    } catch (error) {
      console.error("Error updating resource timesheet rejection:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  };

  // Resource timesheet approval mutation
  const [updateResourceTimesheetApproveMutation] = useMutation(
    TIMESHEET_QUERIES.UPDATE_RESOURCE_TIMESHEET_APPROVE
  );

  /**
   * Approves a resource timesheet (resource-level).
   * @param {Object} approvalData
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateResourceTimesheetApprove = async (approvalData) => {
    try {
      const { data } = await updateResourceTimesheetApproveMutation({
        variables: {
          timesheetWeek: approvalData.timesheet_week,
          resourceId: approvalData.resource_id,
          status: approvalData.status,
          approvedBy: approvalData.approved_by,
        },
      });

      if (!data?.UpdateResourceTimesheetApprove) {
        throw new Error("Failed to approve resource timesheet");
      }

      return {
        success: true,
        data: data.UpdateResourceTimesheetApprove,
      };
    } catch (error) {
      console.error("Error approving resource timesheet:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  };

   const deleteTimesheet = async (weekly_timesheet_id) => {
      try {
        const { data } = await deleteTimesheetMutation({
          variables: { weekly_timesheet_id },
        });
        return { success: true, data: data.DeleteTimesheet };
      } catch (error) {
        console.error("Error deleting timesheet entry:", error);
        return { success: false, error: error.message };
      }
    };

  return {
    /**
     * Add a new weekly timesheet.
     * @function
     * @param {Array} weeklyTimesheetDetails
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    addWeeklyTimesheet,
    /**
     * Get all fridays between two dates.
     * @function
     * @param {number} lastFriday
     * @param {number} upcomingFriday
     * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
     */
    getFridays,
    /**
     * Get Friday / week options for a quarter (e.g. 2024_Q1).
     * @function
     * @param {string} quarter
     * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
     */
    getFridaysByQuarter,
    getFridaysByYearAndMonth,
    getLatestAndOldestTimesheetLoggedDate,
    /**
     * Get a resource's timesheet for a week.
     * @function
     * @param {string} timesheetWeek
     * @param {string|number} resourceId
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    getResourceTimesheet,
    /**
     * Reject a timesheet (project-level).
     * @function
     * @param {Object} rejectionData
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    updateTimesheetRejection,
    /**
     * Approve a timesheet (project-level).
     * @function
     * @param {Object} approvalData
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    updateTimesheetApprove,
    /**
     * Get all resource timesheets for a week and project.
     * @function
     * @param {string} timesheetWeek
     * @param {string|number} projectId
     * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
     */
    getResourceTimesheetsByWeekAndProject,
    /**
     * Get all resource timesheets for a week and resource.
     * @function
     * @param {string} timesheetWeek
     * @param {string|number} resourceId
     * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
     */
    getResourceTimesheetsByWeekAndResource,
    /**
     * Reject a resource timesheet (resource-level).
     * @function
     * @param {Object} rejectionData
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    updateResourceTimesheetRejection,
    /**
     * Approve a resource timesheet (resource-level).
     * @function
     * @param {Object} approvalData
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    updateResourceTimesheetApprove,
    deleteTimesheet
  };
};
